import React, { useEffect, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authApi } from "../services/api";
import AuthCard from "../components/AuthCard";

const MIN_PASSWORD = 8;

function ErrorAlert({ error }) {
  if (!error) return null;
  return (
    <div className="alert alert-danger py-2 small" role="alert">
      {error}
    </div>
  );
}

// Shown when the server has no email service, so nothing actually reaches the inbox.
function NoMailNotice({ mailConfigured }) {
  if (mailConfigured !== false) return null;
  return (
    <div className="alert alert-warning py-2 small" role="status">
      <i className="bi bi-exclamation-triangle me-1" />
      Email sending isn't set up on this server yet, so no email was actually
      sent. The link is printed in the API terminal instead.
    </div>
  );
}

export function CheckEmail() {
  const location = useLocation();
  const { setUser } = useAuth();
  const knownEmail = location.state?.email || "";
  const [email, setEmail] = useState(knownEmail);
  const [mailConfigured, setMailConfigured] = useState(
    location.state?.mailConfigured,
  );
  const [resent, setResent] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  // Clicking the link in another tab signs this browser in, so pick that up when the user comes back.
  useEffect(() => {
    const recheck = () =>
      authApi
        .me()
        .then(({ user }) => setUser(user))
        .catch(() => {});
    window.addEventListener("focus", recheck);
    return () => window.removeEventListener("focus", recheck);
  }, [setUser]);

  const resend = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      const res = await authApi.resendVerification(email.trim());
      setMailConfigured(res.mailConfigured);
      setResent(true);
    } catch (err) {
      setError(err.message);
    }
    setSending(false);
  };

  const intro = !knownEmail
    ? "Enter the email you signed up with and we'll send you a new confirmation link."
    : location.state?.unconfirmed
      ? "Your account isn't confirmed yet. Click the link we sent to"
      : mailConfigured === false
        ? "We made a confirmation link for"
        : "We sent a confirmation link to";

  return (
    <AuthCard
      title="Confirm your email"
      footer={<Link to="/login">Back to log in</Link>}
    >
      <ErrorAlert error={error} />
      <NoMailNotice mailConfigured={mailConfigured} />
      {resent && (
        <div className="alert alert-success py-2 small" role="status">
          If that account still needs confirming, a new link is on its way. Only
          the newest link works.
        </div>
      )}
      <p className="small text-center mb-3">
        {intro}
        {knownEmail && (
          <>
            {" "}
            <span className="fw-medium">{knownEmail}</span>. Click it to finish
            setting up your account and log in. The link works for 24 hours.
          </>
        )}
      </p>
      <form onSubmit={resend} noValidate>
        {!knownEmail && (
          <div className="mb-3">
            <label className="form-label small fw-medium" htmlFor="check-email">
              Email
            </label>
            <input
              id="check-email"
              type="email"
              className="form-control"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
          </div>
        )}
        <button
          type="submit"
          className={`btn w-100 ${knownEmail ? "btn-outline-primary" : "btn-primary"}`}
          disabled={sending || !email.trim()}
        >
          {sending && (
            <span className="spinner-border spinner-border-sm me-2" />
          )}
          Send a new link
        </button>
      </form>
      {knownEmail && (
        <p className="small text-muted text-center mt-3 mb-0">
          Nothing there? Check your spam folder.
        </p>
      )}
    </AuthCard>
  );
}

export function ForgotPassword() {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || "");
  const [sentTo, setSentTo] = useState("");
  const [mailConfigured, setMailConfigured] = useState();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await authApi.forgotPassword(email.trim());
      setMailConfigured(res.mailConfigured);
      setSentTo(email.trim());
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  const footer = <Link to="/login">Back to log in</Link>;

  if (sentTo) {
    return (
      <AuthCard title="Check your email" footer={footer}>
        <NoMailNotice mailConfigured={mailConfigured} />
        <p className="small text-center mb-3">
          If there's an Plateo account for{" "}
          <span className="fw-medium">{sentTo}</span>, we've sent it a link to
          choose a new password. The link works for 1 hour.
        </p>
        <p className="small text-muted text-center mb-0">
          Nothing there? Check your spam folder, or{" "}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 align-baseline"
            onClick={() => setSentTo("")}
          >
            try again
          </button>
          .
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Forgot your password?"
      subtitle="Enter your email and we'll send you a link to choose a new one."
      footer={footer}
    >
      <form onSubmit={submit} noValidate>
        <ErrorAlert error={error} />
        <div className="mb-4">
          <label className="form-label small fw-medium" htmlFor="forgot-email">
            Email
          </label>
          <input
            id="forgot-email"
            type="email"
            className="form-control"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            required
          />
        </div>
        <button
          type="submit"
          className="btn btn-primary w-100"
          disabled={submitting || !email.trim()}
        >
          {submitting && (
            <span className="spinner-border spinner-border-sm me-2" />
          )}
          Send reset link
        </button>
      </form>
    </AuthCard>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(!token);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm)
      return setError("The passwords don't match");
    setError("");
    setSubmitting(true);
    try {
      const { user } = await authApi.resetPassword(token, form.password);
      setUser(user);
      navigate("/", { replace: true });
    } catch (err) {
      if (err.code === "invalid_token") setExpired(true);
      else setError(err.message);
      setSubmitting(false);
    }
  };

  if (expired) {
    return (
      <AuthCard
        title="This link doesn't work anymore"
        footer={<Link to="/login">Back to log in</Link>}
      >
        <p className="small text-center mb-4">
          Reset links work for 1 hour and only once. Ask for a new one and use
          the newest email.
        </p>
        <Link to="/forgot-password" className="btn btn-primary w-100">
          Send a new link
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Choose a new password"
      subtitle="You'll be signed out on your other devices and the extension."
    >
      <form onSubmit={submit} noValidate>
        <ErrorAlert error={error} />
        <div className="mb-3">
          <label
            className="form-label small fw-medium"
            htmlFor="reset-password"
          >
            New password
          </label>
          <input
            id="reset-password"
            type="password"
            className="form-control"
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            minLength={MIN_PASSWORD}
            autoFocus
            required
          />
          <div className="form-text">At least {MIN_PASSWORD} characters.</div>
        </div>
        <div className="mb-4">
          <label className="form-label small fw-medium" htmlFor="reset-confirm">
            Confirm new password
          </label>
          <input
            id="reset-confirm"
            type="password"
            className="form-control"
            autoComplete="new-password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            required
          />
        </div>
        <button
          type="submit"
          className="btn btn-primary w-100"
          disabled={
            submitting || form.password.length < MIN_PASSWORD || !form.confirm
          }
        >
          {submitting && (
            <span className="spinner-border spinner-border-sm me-2" />
          )}
          Save and log in
        </button>
      </form>
    </AuthCard>
  );
}

// Landing page for the "This wasn't me" button in the Gmail-connected security alert.
// Opening the link does nothing by itself (mail scanners pre-open links); only the
// button press disconnects Gmail, revokes access and signs the account out everywhere.
export function GmailSecurity() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState(token ? "confirm" : "invalid");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const secure = async () => {
    setBusy(true);
    setError("");
    try {
      await authApi.gmailWasntMe(token);
      setState("done");
    } catch (err) {
      if (err.code === "invalid_token") setState("invalid");
      else setError(err.message);
      setBusy(false);
    }
  };

  if (state === "invalid") {
    return (
      <AuthCard
        title="This link doesn't work anymore"
        footer={<Link to="/login">Back to log in</Link>}
      >
        <p className="small text-center mb-0">
          Security links work only once. If your Gmail is still connected and
          shouldn't be, sign in and disconnect it from your Profile, then change
          your password.
        </p>
      </AuthCard>
    );
  }
  if (state === "done") {
    return (
      <AuthCard
        title="Your account is secured"
        footer={<Link to="/login">Log in again</Link>}
      >
        <div className="text-center">
          <i
            className="bi bi-shield-check text-success"
            style={{ fontSize: 40 }}
          />
          <p className="small mt-3 mb-0">
            Gmail has been disconnected from Plateo and Plateo's access revoked,
            and the account has been signed out everywhere. Log in again and{" "}
            <strong>change your password</strong> before reconnecting anything.
          </p>
        </div>
      </AuthCard>
    );
  }
  return (
    <AuthCard title="Secure your account?">
      <ErrorAlert error={error} />
      <p className="small">
        If you didn't connect your Gmail to Plateo, press the button below.
        We'll:
      </p>
      <ul className="small">
        <li>
          Disconnect that Gmail from Plateo and revoke Plateo's access at Google
        </li>
        <li>Sign the Plateo account out on every device and the extension</li>
        <li>Email you a confirmation of what was done</li>
      </ul>
      <p className="small text-muted">
        If this <em>was</em> you, just close this page - nothing will change.
      </p>
      <button
        type="button"
        className="btn btn-danger w-100"
        onClick={secure}
        disabled={busy}
      >
        {busy ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" /> Securing…
          </>
        ) : (
          <>
            <i className="bi bi-shield-exclamation me-1" /> This wasn't me -
            secure my account
          </>
        )}
      </button>
    </AuthCard>
  );
}

// Landing page for the "This wasn't me" button in the password-change email.
// Opening the link does nothing by itself; confirming signs the account out
// everywhere, cancels pending one-time links and disconnects Gmail.
export function SecureAccount() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState(token ? "confirm" : "invalid");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const secure = async () => {
    setBusy(true);
    setError("");
    try {
      await authApi.secureAccount(token);
      setState("done");
    } catch (err) {
      if (err.code === "invalid_token") setState("invalid");
      else setError(err.message);
      setBusy(false);
    }
  };

  if (state === "invalid") {
    return (
      <AuthCard
        title="This link doesn't work anymore"
        footer={<Link to="/login">Back to log in</Link>}
      >
        <p className="small text-center mb-0">
          Security links work only once. To be safe, log in, change your
          password from your Profile, and check Gmail is disconnected there if
          it shouldn't be connected.
        </p>
      </AuthCard>
    );
  }
  if (state === "done") {
    return (
      <AuthCard
        title="Your account is secured"
        footer={<Link to="/login">Log in again</Link>}
      >
        <div className="text-center">
          <i
            className="bi bi-shield-check text-success"
            style={{ fontSize: 40 }}
          />
          <p className="small mt-3 mb-0">
            Everything has been signed out, the password-change link has been
            cancelled, and Gmail has been disconnected. Log in and set a new
            password - you're in control again.
          </p>
        </div>
      </AuthCard>
    );
  }
  return (
    <AuthCard title="Secure your account?">
      <ErrorAlert error={error} />
      <p className="small">
        If you didn't request a password change, press the button below. We'll:
      </p>
      <ul className="small">
        <li>Sign your Plateo account out on every device and the extension</li>
        <li>Cancel that password-change link (and any other pending links)</li>
        <li>Disconnect Gmail from Plateo and revoke its access at Google</li>
        <li>Email you a confirmation of what was done</li>
      </ul>
      <p className="small text-muted">
        Your password stays the same until you change it yourself after logging
        in.
      </p>
      <button
        type="button"
        className="btn btn-danger w-100"
        onClick={secure}
        disabled={busy}
      >
        {busy ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" /> Securing…
          </>
        ) : (
          <>
            <i className="bi bi-shield-exclamation me-1" /> This wasn't me -
            secure my account
          </>
        )}
      </button>
    </AuthCard>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, setUser, checking } = useAuth();
  const [state, setState] = useState(token ? "checking" : "invalid");
  const sent = useRef(false);

  useEffect(() => {
    if (!token || checking || sent.current) return;
    sent.current = true;
    authApi
      .verifyEmail(token)
      .then(({ user: verified }) => {
        setUser(verified);
        setState("done");
      })
      .catch(() => setState("invalid"));
  }, [token, checking, setUser]);

  if (state === "checking") {
    return (
      <AuthCard title="Confirming your email…">
        <div className="text-center">
          <span className="spinner-border text-primary" role="status" />
        </div>
      </AuthCard>
    );
  }
  if (state === "done") {
    return (
      <AuthCard
        title="Email confirmed"
        subtitle="Thanks! Your account is all set and you're logged in."
      >
        <Link to="/" className="btn btn-primary w-100">
          Continue to Plateo
        </Link>
      </AuthCard>
    );
  }
  if (user) {
    return (
      <AuthCard
        title="You're already confirmed"
        subtitle="There's nothing else to do."
      >
        <Link to="/" className="btn btn-primary w-100">
          Continue to Plateo
        </Link>
      </AuthCard>
    );
  }
  return (
    <AuthCard
      title="This link doesn't work anymore"
      footer={<Link to="/login">Back to log in</Link>}
    >
      <p className="small text-center mb-4">
        Confirmation links work for 24 hours and only the newest one works. If
        you've already confirmed, just log in.
      </p>
      <Link to="/check-email" className="btn btn-primary w-100">
        Send a new link
      </Link>
    </AuthCard>
  );
}
